import { useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { checkTokenValidity, clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast from "../../components/toast/toast";
import {
  accountStatusBadgeClass,
  getFisherAccountStatus,
  getFisherSubscriptionStatus,
} from "../../utils/fisherAccountStatus";
import { formatDisplayDateTime as formatDateTime } from "../../utils/formatDisplayDate";

const cardRadius = { borderRadius: "14px" };

function StatusBadge({ status }) {
  return (
    <span
      className={`badge border px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center ${accountStatusBadgeClass(status.tone)}`}
    >
      <i className="fa fa-circle fs-9px fa-fw me-5px" />
      {status.label}
    </span>
  );
}

const Pond = () => {
  const fetchPondsPage = useCallback(async ({ search, page, limit }) => {
    try {
      return await axiosInstance.get("api/Pond/GetAllPonds", {
        params: { search, page, limit },
      });
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return { data: { data: [], total: 0 } };
      }
      showErrorToast(error.response?.data?.message || "Failed to fetch ponds");
      throw error;
    }
  }, []);

  const {
    rows: ponds,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
  } = useServerPaginatedList(fetchPondsPage);

  useEffect(() => {
    checkTokenValidity();
  }, []);

  if (isLoading && ponds.length === 0) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <nav aria-label="breadcrumb" className="mb-2">
          <ol className="breadcrumb mb-0 small">
            <li className="breadcrumb-item">
              <Link to="/dashboard" className="text-decoration-none text-muted">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Ponds
            </li>
          </ol>
        </nav>

        <div className="d-flex flex-column flex-md-row align-items-start justify-content-between gap-3 mb-4">
          <div>
            <h1 className="h3 fw-semibold text-dark mb-1">Ponds</h1>
            <p className="text-muted small mb-0">
              All ponds across customer accounts — view account status and manage pond data.
            </p>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-4" style={cardRadius}>
          <div className="card-body p-3 p-md-4">
            <div className="position-relative">
              <div
                className="position-absolute top-50 translate-middle-y text-muted ps-3"
                style={{ zIndex: 2 }}
              >
                <i className="fa fa-search opacity-50" />
              </div>
              <input
                type="search"
                className="form-control form-control-lg ps-5 donezo-search-field"
                style={{ borderRadius: "12px" }}
                placeholder="Search ponds…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm" style={cardRadius}>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4 small text-muted text-uppercase">Name</th>
                  <th className="small text-muted text-uppercase">Location</th>
                  <th className="small text-muted text-uppercase">Account</th>
                  <th className="small text-muted text-uppercase">Account status</th>
                  <th className="small text-muted text-uppercase">Subscription</th>
                  <th className="small text-muted text-uppercase">Created</th>
                  <th className="pe-4 text-end small text-muted text-uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ponds.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      No ponds found.
                    </td>
                  </tr>
                ) : (
                  ponds.map((pond) => (
                    <tr key={pond.id}>
                      <td className="ps-4 fw-medium">{pond.Name}</td>
                      <td>{pond.Location || "—"}</td>
                      <td>{pond.FisherName || "—"}</td>
                      <td>
                        <StatusBadge status={getFisherAccountStatus(pond)} />
                      </td>
                      <td>
                        <StatusBadge status={getFisherSubscriptionStatus(pond)} />
                      </td>
                      <td className="text-muted small">{formatDateTime(pond.createdAt)}</td>
                      <td className="pe-4 text-end">
                        <Link
                          to={`/updatePond/${pond.id}`}
                          className="btn btn-sm btn-outline-primary rounded-pill me-1"
                        >
                          Edit pond
                        </Link>
                        <Link
                          to={`/pondDetailViaPond/${pond.id}`}
                          className="btn btn-sm btn-outline-secondary rounded-pill"
                        >
                          Visits
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListPagination
            currentPage={currentPage}
            totalCount={totalCount}
            entriesPerPage={entriesPerPage}
            onPageChange={setCurrentPage}
            className="px-4"
          />
        </div>
      </div>
    </div>
  );
};

export default Pond;
